import Store from '@/main/store';
import Router from '@/main/router';

import ICheckoutState from '@/interfaces/CheckoutState';
import { ICheckout, ICheckoutCartOffer, ICheckoutCartOfferPayment } from '@/interfaces/Checkout';
import { IOrder } from '@/interfaces/Order';
import { IContract } from '@/interfaces/Contract';
import { IUpsell } from '@/interfaces/Upsell';
import { ITargetContentLink } from '@/interfaces/TargetContentLink';

import { EPaymentMethod, ECardStep } from '@/helpers/enums';
import { pushSelectedOfferToDatalayer, pushPaymentMethodSelectedToDatalayer } from '@/helpers/dataLayer';

import Contract from '@/classes/Checkout/Contract';
import { Offer } from '@/classes/Checkout/Offer';
export default {
  setOfferLoading(state: ICheckoutState, isLoading: boolean) {
    state.offerLoading = isLoading;
  },
  setPaymentLoading(state: ICheckoutState, isLoading: boolean) {
    state.payment.loading = isLoading;
  },
  setCheckout(state: ICheckoutState, checkoutData: ICheckout) {
    state.checkout = checkoutData;
  },
  selectOffer(state: ICheckoutState, offerId: number) {
    const isRootOffer = state.checkout.cart.offer.id === offerId;
    let selectedOffer = state.checkout.cart.offer;

    if (!isRootOffer) {
      state.checkout.cart.offer.is_selected = false;
    } else {
      state.checkout.cart.offer.is_selected = true;
    }

    if (state.checkout.cart.offer.related_offers) {
      const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

      state.checkout.cart.offer.related_offers = relatedOffers.map(offer => {
        if (offer.id === offerId) {
          selectedOffer = new Offer({ ...offer }, offer.cart_info, offer.price_info);
        }

        return new Offer({
          ...offer,
          is_selected: offer.id === offerId,
        }, offer.cart_info, offer.price_info);
      });
    }

    pushSelectedOfferToDatalayer(
      state.checkout.cart.offer,
      selectedOffer,
    );
  },
  selectOfferPaymentMethod(state: ICheckoutState, options: { offer_id?: number, method: EPaymentMethod }) {
    const rootOffer = state.checkout.cart.offer;
    const relatedOffers = rootOffer.related_offers as ICheckoutCartOffer[];
    let selectedOffer = rootOffer;

    const relatedSelected = relatedOffers && relatedOffers.find(relatedOffer => Boolean(relatedOffer.is_selected));

    if (relatedSelected) {
      selectedOffer = relatedSelected;
    }

    if (!selectedOffer.payment.hasMethod(options.method)) {
      return;
    }

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectPaymentMethod(options.method);
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectPaymentMethod(options.method);
        }

        return offer;
      });
    }

    const selectedOfferPaymentStructure: ICheckoutCartOfferPayment = Store.getters['App/Checkout/selectedOfferPaymentStructure'];

    selectedOffer.invoice.build(selectedOfferPaymentStructure, selectedOffer.payment);

    pushPaymentMethodSelectedToDatalayer(
      selectedOffer,
      options.method,
    );
  },
  selectOfferPaymentCard(state: ICheckoutState, options: { offer_id?: number, card_id: number }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectPaymentCard({ cardId: options.card_id });
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectPaymentCard({ cardId: options.card_id });
        }

        return offer;
      });
    }
  },
  selectOfferPaymentSecondCard(state: ICheckoutState, options: { offer_id?: number, card_id: number }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectPaymentSecondCard({ cardId: options.card_id });
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectPaymentSecondCard({ cardId: options.card_id });
        }

        return offer;
      });
    }
  },
  selectOfferPaymentCardFromLastDigits(state: ICheckoutState, options: { offer_id?: number, last_digits: number }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectPaymentCard({ lastDigits: options.last_digits });
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectPaymentCard({ lastDigits: options.last_digits });
        }

        return offer;
      });
    }
  },
  selectOfferPaymentCardStep(state: ICheckoutState, options: { offer_id?: number, step: ECardStep }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectPaymentCardStep(options.step);
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectPaymentCardStep(options.step);
        }

        return offer;
      });
    }
  },
  selectOfferPaymentCardInstallment(state: ICheckoutState, options: { offer_id?: number, installment: number }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectPaymentCardInstallment(options.installment);
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectPaymentCardInstallment(options.installment);
        }

        return offer;
      });
    }
  },
  setSelectedOfferCoupon(state: ICheckoutState, coupon: string | null) {
    const query = Object.keys(Router.currentRoute.query)
      .filter(param => param !== 'cupom')
      .reduce((a, b) => ({ ...a, [b]: Router.currentRoute.query[b] }), {});

    Router.replace({ query: { ...query, ...coupon && { cupom: coupon } } });
  },
  setOrder(state: ICheckoutState, order: IOrder) {
    state.order = order;
  },
  setContract(state: ICheckoutState, contract: IContract) {
    state.contract = new Contract(contract);
  },
  setTargetContentLink(state: ICheckoutState, targetContentLink: ITargetContentLink) {
    state.targetContentLink = targetContentLink;
  },
  setUpsell(state: ICheckoutState, upsell: IUpsell) {
    state.upsell = upsell;
  },
  reRenderCheckout(state: ICheckoutState) {
    state.reRenderKey = state.reRenderKey + 1;
  },
  toggleModalPayment(state: ICheckoutState) {
    state.modalPayment = !state.modalPayment;
  },
  closeModalPayment(state: ICheckoutState) {
    state.modalPayment = false;
  },
  showUpsellModal(state: ICheckoutState) {
    state.modalUpsell = true;
  },
  closeUpsellModal(state: ICheckoutState) {
    state.modalUpsell = false;
  },
  setOrderFormTemplate(state: ICheckoutState, template: string) {
    state.orderForm.template = template;
  },
  setOrderFormStyles(state: ICheckoutState, styles: string[]) {
    state.orderForm.styles = styles;
  },
  setOrderFormScripts(state: ICheckoutState, scripts: string[]) {
    state.orderForm.scripts = scripts;
  },
  setIsWhiteLabel(state: ICheckoutState) {
    let subdomain = window.location.origin;
    subdomain = subdomain.split('.')[0].replace('https://', '');

    subdomain.includes('ckt') ? state.isWhiteLabel = true : state.isWhiteLabel = false;
  },
  setEnableHiddenMethods(state: ICheckoutState, enableHiddenMethods: boolean) {
    state.enableHiddenMethods = enableHiddenMethods;
  },
  setKoinStepsActivated(state: ICheckoutState, status: boolean) {
    state.koin.activated = status;
  },
  setKoinOptinChecked(state: ICheckoutState, status: boolean) {
    state.koin.checked = status;
  },
  setBankSlipInstallmentsStep(state: ICheckoutState, step: string) {
    state.koin.step = step;
  },
  selectOfferPaymentBankSlipInstallment(state: ICheckoutState, options: { offer_id?: number, installment: any }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectPaymentBankSlipInstallment(options.installment);
      state.koin.installment = options.installment.number;
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectPaymentBankSlipInstallment(options.installment);
          state.koin.installment = options.installment.number;
        }

        return offer;
      });
    }
  },
  setBankSlipInstallment(state: ICheckoutState, installment: any) {
    state.koin.installment = installment;
  },
  setFraudId(state: ICheckoutState, fraudId: string) {
    state.koin.fraud_id = fraudId;
    state.checkout.cart.offer.payment.setKoinFraudId(fraudId);
  },
  setFilterCreditCard(state: ICheckoutState, filter: string | null) {
    state.creditCard.filterActived = filter;
  },
  setRefCreditCardFormActived(state: ICheckoutState, ref: string | null) {
    state.creditCard.refFormActived = ref;
  },
  selectAmountPaymentCard(state: ICheckoutState, options: { offer_id?: number, amount: number }) {
    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectAmountPaymentCard({ amount: options.amount });
    }
  },
  selectPaymentConditionId(state: ICheckoutState, options: { offer_id?: number, payment_condition_id: number, payment_method: EPaymentMethod }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectPaymentConditionId({ paymentMethod: options.payment_method, offerPaymentConditionId: options.payment_condition_id });
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectPaymentConditionId({ paymentMethod: options.payment_method, offerPaymentConditionId: options.payment_condition_id });
        }

        return offer;
      });
    }
  },
  selectMainPaymentCondition(state: ICheckoutState, options: { offer_id?: number, main_payment_condition: any | null }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectMainPaymentCondition({ mainPaymentCondition: options.main_payment_condition });
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectMainPaymentCondition({ mainPaymentCondition: options.main_payment_condition });
        }

        return offer;
      });
    }
  },
  selectSecondPaymentCondition(state: ICheckoutState, options: { offer_id?: number, second_payment_condition: any | null }) {
    const relatedOffers = state.checkout.cart.offer.related_offers as ICheckoutCartOffer[];

    if (options.offer_id === state.checkout.cart.offer.id) {
      state.checkout.cart.offer.payment.selectSecondPaymentCondition({ secondPaymentCondition: options.second_payment_condition });
    } else if (relatedOffers) {
      state.checkout.cart.offer.related_offers = relatedOffers.map((offer: ICheckoutCartOffer) => {
        if (offer.id === options.offer_id) {
          offer.payment.selectSecondPaymentCondition({ secondPaymentCondition: options.second_payment_condition });
        }

        return offer;
      });
    }
  },
  setPaymentConditionId(state: ICheckoutState, paymentConditionId: number | null) {
    return state.paymentConditionIdSelected = paymentConditionId;
  },
  setMainPaymentCondition(state: ICheckoutState, mainPaymentCondition: any | null) {
    return state.mainPaymentCondition = mainPaymentCondition;
  },
  setSecondPaymentCondition(state: ICheckoutState, secondPaymentCondition: any | null) {
    return state.secondPaymentCondition = secondPaymentCondition;
  }
};
